import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Filter, User as UserIcon, Package, AlertCircle } from "lucide-react";
import { ViewSwitcher } from "@/components/common/ViewSwitcher";
import { SearchBar } from "@/components/common/SearchBar";
import { ActiveFilters } from "@/components/common/ActiveFilters";
import { OnboardingGridView } from "@/components/onboarding/OnboardingGridView";
import { OnboardingListView } from "@/components/onboarding/OnboardingListView";
import { OnboardingKanban } from "@/components/onboarding/OnboardingKanban";
import OnboardingSessionManager from "@/components/onboarding/OnboardingSessionManager";
import ItemEligibilityForm from "@/components/deals/ItemEligibilityForm";

interface OnboardingSession {
  id: string;
  status: string;
  created_at: string;
}

interface OnboardingItem {
  id: string;
  status: string;
  catalog_id: string;
  deal_id: string;
  eligibility: any;
  catalog: {
    id: string;
    name: string;
    type: string;
  } | null;
  deal: {
    id: string;
    name: string;
    contact_id: string;
    sales_consultant: string;
    contact: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      primary_email: string | null;
      primary_phone: string | null;
    } | null;
    sales_consultant_user: {
      id: string;
      first_name: string;
      last_name: string;
      display_name: string;
    } | null;
  } | null;
  onboarding_sessions?: OnboardingSession[];
}

// Adapter para converter OnboardingItem para o formato esperado pelos componentes de view
const adaptItemForView = (item: OnboardingItem) => ({
  id: item.id,
  status: item.status,
  catalog_id: item.catalog_id,
  deal_id: item.deal_id,
  eligibility: item.eligibility,
  catalog: item.catalog ? {
    name: item.catalog.name,
    description: null,
  } : null,
  deal: item.deal ? {
    id: item.deal.id,
    deal_name: item.deal.name,
    contacts: item.deal.contact ? {
      id: item.deal.contact.id,
      first_name: item.deal.contact.first_name,
      last_name: item.deal.contact.last_name,
    } : null,
    owner: item.deal.sales_consultant_user ? {
      id: item.deal.sales_consultant_user.id,
      first_name: item.deal.sales_consultant_user.first_name,
      last_name: item.deal.sales_consultant_user.last_name,
      display_name: item.deal.sales_consultant_user.display_name,
    } : null,
  } : null,
  onboarding_sessions: item.onboarding_sessions,
});

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItem, setSelectedItem] = useState<OnboardingItem | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table'>('table');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [myOnboardingsFilter, setMyOnboardingsFilter] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: appUser } = await supabase
          .from('app_users')
          .select('id')
          .eq('auth_user_id', user.id)
          .single();
        if (appUser) {
          setCurrentUserId(appUser.id);
        }
      }
    };
    getUser();
  }, []);

  const { data: items, isLoading, isError, error } = useQuery({
    queryKey: ["onboarding-items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("items")
        .select(`
          id,
          status,
          catalog_id,
          deal_id,
          eligibility,
          catalog:catalog_id (
            id,
            name,
            type
          ),
          deal:deal_id (
            id,
            name,
            contact_id,
            sales_consultant,
            contact:contact_id (
              id,
              first_name,
              last_name,
              primary_email,
              primary_phone
            ),
            sales_consultant_user:sales_consultant (
              id,
              first_name,
              last_name,
              display_name
            )
          ),
          onboarding_sessions (
            id,
            status,
            created_at
          )
        `)
        .in("status", ["pending_onboarding", "onboarded", "active"])
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao carregar itens de onboarding:", error);
        throw error;
      }
      return data as unknown as OnboardingItem[];
    },
  });

  const filteredItems = items?.filter((item) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const contactName = item.deal?.contact
        ? [item.deal.contact.first_name, item.deal.contact.last_name].filter(Boolean).join(" ")
        : "";
      const matchesSearch = 
        item.catalog?.name?.toLowerCase().includes(query) ||
        item.deal?.name?.toLowerCase().includes(query) ||
        contactName.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== 'all') {
      const hasCompletedSession = item.onboarding_sessions?.some(s => s.status === 'completed');
      if (statusFilter === 'completed' && !hasCompletedSession) return false;
      if (statusFilter === 'pending' && hasCompletedSession) return false;
    }

    // My onboardings filter
    if (myOnboardingsFilter && currentUserId) {
      if (item.deal?.sales_consultant !== currentUserId) return false;
    }

    return true;
  });

  const adaptedItems = filteredItems?.map(adaptItemForView) || [];

  const handleRemoveFilter = (filterKey: string) => {
    if (filterKey === 'all') {
      setSearchQuery('');
      setStatusFilter('all');
      setMyOnboardingsFilter(false);
      return;
    }
    
    switch (filterKey) {
      case 'searchQuery':
        setSearchQuery('');
        break;
      case 'statusFilter':
        setStatusFilter('all');
        break;
      case 'myOnboardingsFilter':
        setMyOnboardingsFilter(false);
        break;
    }
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || myOnboardingsFilter;
  const handleViewItem = (adaptedItem: ReturnType<typeof adaptItemForView>) => {
    const originalItem = items?.find(i => i.id === adaptedItem.id);
    if (originalItem) {
      // Check if has active session - go to detail page
      const hasSession = originalItem.onboarding_sessions && originalItem.onboarding_sessions.length > 0;
      if (hasSession) {
        navigate(`/onboarding/detail?id=${originalItem.id}`);
      } else {
        // No session - open modal to schedule
        setSelectedItem(originalItem);
      }
    }
  };

  const renderContent = () => {
    if (isError) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Erro ao carregar onboarding
            </h3>
            <p className="text-muted-foreground mb-4">
              {error instanceof Error ? error.message : "Ocorreu um erro inesperado."}
            </p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Recarregar página
            </Button>
          </CardContent>
        </Card>
      );
    }

    switch (viewMode) {
      case 'list':
        return (
          <OnboardingListView
            items={adaptedItems}
            isLoading={isLoading}
            onViewItem={handleViewItem}
          />
        );
      case 'table':
        return (
          <OnboardingKanban
            items={adaptedItems}
            isLoading={isLoading}
            onViewItem={handleViewItem}
          />
        );
      default:
        return (
          <OnboardingGridView
            items={adaptedItems}
            isLoading={isLoading}
            onViewItem={handleViewItem}
          />
        );
    }
  };

  return (
    <div className="flex-shrink-0 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto p-6 space-y-4">
        {/* Active Filters */}
        <ActiveFilters
          filters={{
            searchQuery,
            statusFilter: statusFilter !== 'all' ? statusFilter : undefined,
            myOnboardingsFilter,
          }}
          onRemoveFilter={handleRemoveFilter}
          module="onboarding"
        />

        {/* Header Controls */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search Bar */}
          <div className="flex-1 max-w-md">
            <SearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por cliente, produto..."
            />
          </div>

          {/* Controls */}
          <div className="flex gap-2 items-center flex-wrap">
            {/* Add Button */}
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full h-9 w-9 p-0 flex-shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" />
            </Button>

            {/* My Onboardings Button */}
            <Button
              variant={myOnboardingsFilter ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMyOnboardingsFilter(!myOnboardingsFilter)}
              className="flex items-center gap-2 h-9 rounded-full"
            >
              <UserIcon className="w-4 h-4" />
              Meus Onboardings
            </Button>

            {/* Status Toggle */}
            <div className="flex items-center border border-border rounded-full overflow-hidden h-9 bg-background">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStatusFilter('pending')}
                className={`rounded-none h-full px-3 text-xs border-0 ${
                  statusFilter === 'pending' 
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                    : 'hover:bg-muted'
                }`}
              >
                Pendentes
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStatusFilter('completed')}
                className={`rounded-none h-full px-3 text-xs border-l border-border ${
                  statusFilter === 'completed' 
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                    : 'hover:bg-muted'
                }`}
              >
                Concluídos
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStatusFilter('all')}
                className={`rounded-none h-full px-3 text-xs border-l border-border ${
                  statusFilter === 'all' 
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                    : 'hover:bg-muted'
                }`}
              >
                Todos
              </Button>
            </div>

            {/* Filter Button */}
            <Button
              variant="ghost"
              size="sm"
              className={`rounded-full h-9 w-9 p-0 flex-shrink-0 ${
                hasActiveFilters
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'hover:bg-muted'
              }`}
            >
              <Filter className="w-4 h-4" />
            </Button>

            <ViewSwitcher viewMode={viewMode} onViewChange={setViewMode} />
          </div>
        </div>

      {/* Content */}
      {renderContent()}

      {/* Modal de Detalhes do Item */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {selectedItem?.catalog?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedItem && (
            <Tabs defaultValue="onboarding" className="mt-4">
              <TabsList>
                <TabsTrigger value="onboarding">Sessões de Onboarding</TabsTrigger>
                <TabsTrigger value="eligibility">Elegibilidade</TabsTrigger>
              </TabsList>

              <TabsContent value="onboarding" className="mt-4">
                <OnboardingSessionManager
                  itemId={selectedItem.id}
                  itemName={selectedItem.catalog?.name}
                />
              </TabsContent>

              <TabsContent value="eligibility" className="mt-4">
                <ItemEligibilityForm
                  itemId={selectedItem.id}
                  catalogId={selectedItem.catalog_id}
                  currentEligibility={selectedItem.eligibility}
                />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
